/**
 * Discrete cue map.
 *
 * Each export is a fire-and-forget function. If the engine isn't unlocked
 * (no user gesture yet) the underlying synth helpers no-op silently, so
 * callers never need to check.
 *
 * Per AESTHETIC.md §7: warm, never sharp; never percussive; never a sting.
 * Each cue here was tuned against the spec in TASK-13 — pitches and
 * durations match the brief so they can be regression-checked from the doc.
 */

import { engine } from './engine';
import { playTone, playChord, playNoise, makeFilter } from './synth';

/** Tiny soft pop — 250Hz sine, ~60ms exp decay. The chat bubble. */
function chatPop(): void {
  playTone({
    freq: 250,
    duration: 0.06,
    peak: 0.22,
    attack: 0.008,
    type: 'sine',
  });
}

/** Low chime — 340Hz fundamental + 1020Hz (3rd harmonic), 200ms. */
function mushroomPickup(): void {
  playChord(
    { freq: 340, duration: 0.2, peak: 0.3, attack: 0.01, type: 'sine' },
    { freq: 1020, duration: 0.18, peak: 0.08, attack: 0.012, type: 'sine' },
  );
}

/** Faint underwater bloop — low-pass filtered 180Hz blip. */
function fishNibble(): void {
  const ctx = engine.context();
  const cueBus = engine.cueBus();
  if (!ctx || !cueBus) return;

  // Low-pass to soften the tone into "underwater."
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 420;
  lp.Q.value = 0.9;
  lp.connect(cueBus);

  playTone({
    freq: 180,
    duration: 0.16,
    peak: 0.32,
    attack: 0.014,
    type: 'sine',
    destination: lp,
  });

  // Tiny pitch sag for the "bloop" character.
  const ctxNow = ctx.currentTime;
  setTimeout(() => {
    try {
      lp.disconnect();
    } catch {
      // Already disconnected.
    }
  }, 240);
  void ctxNow;
}

/** Two-note rising chime — 440Hz then 660Hz, ~400ms total. */
function fishCatch(): void {
  playTone({
    freq: 440,
    duration: 0.22,
    peak: 0.28,
    attack: 0.012,
    type: 'sine',
  });
  playTone({
    freq: 660,
    duration: 0.28,
    peak: 0.26,
    attack: 0.012,
    type: 'sine',
    delay: 0.13,
  });
  // Soft 5th harmonic on the second note for "bell-ness" without brightness.
  playTone({
    freq: 1320,
    duration: 0.2,
    peak: 0.05,
    attack: 0.02,
    type: 'sine',
    delay: 0.13,
  });
}

/** Soft three-note arpeggio — C5/E5/G5 at 523/659/784Hz, ~600ms total. */
function unlock(): void {
  const notes: Array<{ freq: number; delay: number }> = [
    { freq: 523, delay: 0 },
    { freq: 659, delay: 0.18 },
    { freq: 784, delay: 0.36 },
  ];
  for (const { freq, delay } of notes) {
    playTone({
      freq,
      duration: 0.25,
      peak: 0.26,
      attack: 0.015,
      type: 'sine',
      delay,
    });
    // Soft octave-up ghost for chime character.
    playTone({
      freq: freq * 2,
      duration: 0.22,
      peak: 0.045,
      attack: 0.02,
      type: 'sine',
      delay,
    });
  }
}

/** Sustained bell-like — 880Hz, slow attack, gentle ~1s decay. */
function dailyDrop(): void {
  // Fundamental — slow attack for the "bell swell."
  playTone({
    freq: 880,
    duration: 1.0,
    peak: 0.3,
    attack: 0.08,
    type: 'sine',
  });
  // Octave above and a soft fifth harmonic underneath for body.
  playTone({
    freq: 1760,
    duration: 0.9,
    peak: 0.06,
    attack: 0.09,
    type: 'sine',
  });
  playTone({
    freq: 440,
    duration: 1.1,
    peak: 0.09,
    attack: 0.1,
    type: 'sine',
  });
}

/** Soft whoosh — band-passed noise sweeping 400 -> 1200Hz, ~500ms. */
function portalStep(): void {
  const ctx = engine.context();
  const cueBus = engine.cueBus();
  if (!ctx || !cueBus) return;

  const filter = makeFilter('bandpass', 400, 1.2);
  if (!filter) return;
  filter.connect(cueBus);

  const result = playNoise({
    duration: 0.5,
    peak: 0.22,
    attack: 0.06,
    filter,
  });
  if (!result) {
    try {
      filter.disconnect();
    } catch {
      // Already disconnected.
    }
    return;
  }

  // Sweep the filter's frequency 400 -> 1200 over the cue's life.
  const { start } = result;
  filter.frequency.setValueAtTime(400, start);
  filter.frequency.linearRampToValueAtTime(1200, start + 0.5);

  // Filter disconnect happens after the source ends — give it a safe margin.
  setTimeout(() => {
    try {
      filter.disconnect();
    } catch {
      // Already disconnected.
    }
  }, 700);
}

/** Barely-audible bell tap — 660Hz, very short, very quiet. */
function emoteWave(): void {
  playTone({
    freq: 660,
    duration: 0.12,
    peak: 0.14,
    attack: 0.01,
    type: 'sine',
  });
  // Tiny harmonic shimmer.
  playTone({
    freq: 1320,
    duration: 0.1,
    peak: 0.03,
    attack: 0.012,
    type: 'sine',
  });
}

export const sounds = {
  chatPop,
  mushroomPickup,
  fishNibble,
  fishCatch,
  unlock,
  dailyDrop,
  portalStep,
  emoteWave,
};
